import {
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  MouseEvent,
} from 'react';
import { useDropzone } from 'react-dropzone';

import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import IconUpload from 'approot/shared/icons/icon-upload';
import {
  getAcceptError,
  getAcceptExtensions,
  getOptionalProps,
} from './file-dropper.utils';
import {
  ACCEPT_TYPE_AUDIO,
  AcceptTypes,
  FILE_SIZE_ERROR_CODE,
  FILE_TYPE_ERROR_CODE,
  Props,
} from './file-dropper.constants';
import * as Styled from './file-dropper.style';
import ProgressIndicator from 'approot/shared/progress-bar/progress-indicator';
import RoundButton from 'approot/shared/round-icon-button/round-icon-button';
import {
  LinkButton,
  SecondaryButton,
} from 'approot/shared/styles/buttons/buttons';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import {
  useFileDropperContext,
  FileDropperContextProvider,
} from './file-dropper.context';
import { IconError } from 'approot/shared/inline-svg/icons';
import IconCircleCheck from 'approot/shared/icons/icon-circle-check';
import { grey1 } from 'approot/shared/styles/colours.style';
import { useFileDropperHandlers } from './file-dropper.hooks';
import useFileAudioDuration from './use-file-audio-duration';
import FileDropperAudioButton from './file-dropper-audio-button';
import IconFileImage from 'approot/shared/icons/icon-file-image';
import IconTrash from 'approot/shared/icons/icon-trash';

export const FileDropper = forwardRef<{ click: () => void }, Props>(
  (
    {
      square,
      thumbnailStyling,
      label,
      source,
      onChange,
      name,
      placeholder,
      accept,
      value,
      error,
      validationError,
      isLoading,
      isFinishing,
      disabled,
      maxSizeInMb,
      onSelectPagePreview,
      onFileRemoved,
      skipS3Upload,
    }: Props,
    ref
  ) => {
    const [existingFile, setExistingFile] = useState<string | undefined>(value);
    const [preUploadError, setPreUploadError] = useState<string | undefined>(
      undefined
    );

    const { file, error: uploadError } = useFileDropperContext(name);

    const { onDrop, inputRef, launchFileDialog, inputId, progress } =
      useFileDropperHandlers({
        name,
        onChange,
        accept,
        skipS3Upload,
      });

    useImperativeHandle(ref, () => ({
      click: () => {
        inputRef.current?.click();
      },
    }));

    const optionalProps = getOptionalProps(accept);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: (files, fileRejections) => {
        if (fileRejections.length > 0) {
          const errorCode = fileRejections[0].errors[0].code;
          if (maxSizeInMb && errorCode === FILE_SIZE_ERROR_CODE) {
            setPreUploadError(`File exceeds max size of ${maxSizeInMb}MB`);
          } else if (errorCode === FILE_TYPE_ERROR_CODE) {
            setPreUploadError(getAcceptError(accept || []));
          }
        }
        onDrop(files, fileRejections);
      },
      multiple: false,
      disabled,
      maxSize: maxSizeInMb ? maxSizeInMb * (1024 * 1024) : undefined,
      ...optionalProps,
    });

    const hasFileError = preUploadError || error || uploadError;
    const displayError = hasFileError || validationError;

    return (
      <>
        {existingFile ? (
          <FileDropperUploaded
            fileName={existingFile}
            source={source}
            accept={accept}
            onClear={() => {
              onFileRemoved?.();
              setExistingFile(undefined);
            }}
          />
        ) : file ? (
          <FileDropperProgress
            file={file}
            name={name}
            progress={progress}
            isFinishing={isFinishing}
            source={URL.createObjectURL(file)}
            accept={accept}
            error={uploadError}
            onClearFile={onFileRemoved}
          />
        ) : (
          <>
            {label ? (
              <Styled.FileDropperLabel htmlFor={inputId}>
                {label}
              </Styled.FileDropperLabel>
            ) : null}
            <Styled.FileDropperField
              {...getRootProps({
                onClick: (e: MouseEvent) => {
                  e.stopPropagation();
                  launchFileDialog();
                },
              })}
              {...optionalProps}
              $activeDrag={isDragActive}
              $thumbnailStyling={thumbnailStyling}
              $square={square}
              $isLoading={isLoading}
              $hasError={!!(displayError || validationError)}
            >
              <Styled.FileDropperInput
                id={inputId}
                name={name}
                data-selector={name}
                {...getInputProps()}
                disabled={disabled}
                data-testid="file-dropper-input"
                aria-label="Select a file to upload"
                ref={inputRef}
                onClick={() => {
                  if (hasFileError && inputRef && inputRef.current) {
                    inputRef.current.value = '';
                  }
                }}
                aria-describedby={`file-error-${name}`}
                aria-invalid={!!displayError}
              />
              <Styled.FileDropperPlaceholder>
                <IconUpload width={24} height={24} />
                {placeholder ? (
                  placeholder
                ) : (
                  <Styled.FileDropperText>
                    {placeholder || 'Drag and drop a file here'} or{' '}
                    <Styled.FileDropperPlaceholderButton
                      type="button"
                      disabled={disabled}
                    >
                      browse.
                    </Styled.FileDropperPlaceholderButton>
                  </Styled.FileDropperText>
                )}
                {accept && (
                  <Styled.FileDropperSecondaryText>
                    Files supported: {getAcceptExtensions(accept)}
                  </Styled.FileDropperSecondaryText>
                )}
              </Styled.FileDropperPlaceholder>
              {(displayError || validationError) && (
                <ErrorMessageStyled
                  id={`file-error-${name}`}
                  data-selector={`file-error-${name}`}
                >
                  {displayError}
                </ErrorMessageStyled>
              )}
            </Styled.FileDropperField>
            {onSelectPagePreview && (
              <Styled.FileDropperButtons>
                {onSelectPagePreview && (
                  <SecondaryButton
                    type="button"
                    $isSmall
                    onClick={onSelectPagePreview}
                  >
                    Select from Preview
                  </SecondaryButton>
                )}
              </Styled.FileDropperButtons>
            )}
          </>
        )}
      </>
    );
  }
);

const FileDropperRemoveIcon = ({
  hoverText,
  onClick,
}: {
  hoverText: string;
  onClick?: () => void;
}) => (
  <TooltipWrapper hoverText={hoverText}>
    <RoundButton
      selected={false}
      aria-label="Delete"
      icon={<IconTrash width={24} height={24} />}
      isNegative
      noBorder
      onClick={onClick}
      data-selector="action-delete"
    />
  </TooltipWrapper>
);

const FileDropperUploaded = ({
  fileName,
  source,
  accept,
  onClear,
}: {
  fileName: string;
  source?: string;
  accept?: AcceptTypes;
  onClear: () => void;
}) => {
  const { audioDurationLabel } = useFileAudioDuration({
    source,
    accept,
  });

  const isAudio = source && accept?.includes(ACCEPT_TYPE_AUDIO);

  return (
    <Styled.FileDropper>
      <Styled.FileDropperProgress>
        {isAudio ? (
          <Styled.FileDropperProgressIndicator>
            <FileDropperAudioButton audioUrl={source} description={fileName} />
          </Styled.FileDropperProgressIndicator>
        ) : (
          <Styled.FileDropperProgressIndicator>
            <IconFileImage width={24} height={24} />
          </Styled.FileDropperProgressIndicator>
        )}
        <Styled.FileDropperProgressDetail>
          <Styled.FileDropperProgressName
            $maxLines={1}
            data-selector="uploaded-file-name"
          >
            {fileName}
          </Styled.FileDropperProgressName>
          {source && accept?.includes(ACCEPT_TYPE_AUDIO) && (
            <Styled.FileDropperSecondaryText>
              {audioDurationLabel}
            </Styled.FileDropperSecondaryText>
          )}
        </Styled.FileDropperProgressDetail>
        <Styled.FileDropperProgressActions>
          <FileDropperRemoveIcon hoverText="Remove" onClick={onClear} />
        </Styled.FileDropperProgressActions>
      </Styled.FileDropperProgress>
    </Styled.FileDropper>
  );
};

export const FileDropperProgress = ({
  file,
  name,
  progress,
  isFinishing,
  error,
  source,
  accept,
  onClearFile,
}: {
  file: File;
  name: string;
  progress: ProgressEvent | undefined;
  isFinishing?: boolean;
  error?: string;
  source?: string;
  accept?: AcceptTypes;
  onClearFile?: () => void;
}) => {
  const { xhrObject, clearFile } = useFileDropperContext(name);
  const { audioDurationLabel } = useFileAudioDuration({
    source,
    accept,
  });

  const isAudio = source && accept?.includes(ACCEPT_TYPE_AUDIO);
  const isUploaded = progress ? progress.loaded === progress.total : false;

  const handleClearFile = useCallback(() => {
    clearFile();
    onClearFile?.();
  }, [clearFile, onClearFile]);

  return (
    <Styled.FileDropper $backgroundColor={grey1}>
      <Styled.FileDropperProgress>
        <Styled.FileDropperProgressIndicator>
          {error ? (
            <IconError />
          ) : isUploaded && isAudio ? (
            <FileDropperAudioButton audioUrl={source} description={file.name} />
          ) : isUploaded ? (
            <IconCircleCheck width={24} height={24} />
          ) : (
            <IconFileImage width={24} height={24} />
          )}
        </Styled.FileDropperProgressIndicator>
        <Styled.FileDropperProgressDetail>
          <Styled.FileDropperProgressName $maxLines={1}>
            {file.name}
          </Styled.FileDropperProgressName>
          <Styled.FileDropperProgressSize>
            {error ? (
              <Styled.FileDropperProgressError
                data-selector={`file-error-${name}`}
              >
                {error}{' '}
                <LinkButton type="button" $error onClick={handleClearFile}>
                  Try again
                </LinkButton>
              </Styled.FileDropperProgressError>
            ) : isFinishing ? (
              <span>Finishing up</span>
            ) : isUploaded ? (
              <>
                {source && accept?.includes(ACCEPT_TYPE_AUDIO) ? (
                  <Styled.FileDropperSecondaryText>
                    {audioDurationLabel}
                  </Styled.FileDropperSecondaryText>
                ) : (
                  <span>Uploaded Successfully</span>
                )}
              </>
            ) : (
              `${(file.size / 1024 / 1024).toFixed(2)}MB`
            )}
          </Styled.FileDropperProgressSize>
          {!error && !isUploaded && (
            <Styled.FileDropperProgressBar>
              <ProgressIndicator
                progress={progress?.loaded || 0}
                total={progress?.total || 0}
              />
              <Styled.FileDropperProgressBarAmount>
                {progress
                  ? Math.floor((progress.loaded / progress.total) * 100)
                  : 0}
                %
              </Styled.FileDropperProgressBarAmount>
            </Styled.FileDropperProgressBar>
          )}
        </Styled.FileDropperProgressDetail>
        <Styled.FileDropperProgressActions>
          <FileDropperRemoveIcon
            hoverText={isUploaded || error ? 'Remove' : 'Cancel'}
            onClick={() => {
              if (!isUploaded) {
                xhrObject?.abort();
              }
              handleClearFile();
            }}
          />
        </Styled.FileDropperProgressActions>
      </Styled.FileDropperProgress>
    </Styled.FileDropper>
  );
};

export { useFileDropperContext, FileDropperContextProvider };
